/**
 * IndexedDB 适配器实现（V1 默认存储）
 * ------------------------------------------------------------
 * 只实现 adapter.js 定义的契约，不暴露任何 IndexedDB 细节给上层。
 */
import { STORE_SCHEMA, SCHEMA_VERSION } from '../schema.js';

const DB_NAME = 'english-learning';

const req = r => new Promise((resolve, reject) => {
  r.onsuccess = () => resolve(r.result);
  r.onerror = () => reject(r.error);
});

export class IndexedDBAdapter {
  constructor() {
    this.kind = 'indexeddb';
    this._dbPromise = null;
  }

  _open() {
    if (this._dbPromise) return this._dbPromise;
    this._dbPromise = new Promise((resolve, reject) => {
      const openReq = indexedDB.open(DB_NAME, SCHEMA_VERSION);
      openReq.onupgradeneeded = (e) => {
        const db = e.target.result;
        for (const [name, cfg] of Object.entries(STORE_SCHEMA)) {
          const store = db.objectStoreNames.contains(name)
            ? e.target.transaction.objectStore(name)
            : db.createObjectStore(name, { keyPath: cfg.keyPath });
          for (const idx of cfg.indexes || []) {
            if (!store.indexNames.contains(idx)) {
              store.createIndex(idx, idx, { unique: false });
            }
          }
        }
      };
      openReq.onsuccess = () => resolve(openReq.result);
      openReq.onerror = () => reject(openReq.error);
      openReq.onblocked = () => reject(new Error('数据库升级被其他标签页阻塞，请关闭其它页面后刷新'));
    });
    return this._dbPromise;
  }

  async _tx(name, mode, fn) {
    const db = await this._open();
    const tx = db.transaction(name, mode);
    const store = tx.objectStore(name);
    const result = fn(store);
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return result && result.__r ? result.__r.result : undefined;
  }

  async list(name) {
    const db = await this._open();
    return req(db.transaction(name, 'readonly').objectStore(name).getAll());
  }

  async get(name, key) {
    const db = await this._open();
    return (await req(db.transaction(name, 'readonly').objectStore(name).get(key))) ?? null;
  }

  async put(name, value) {
    await this._tx(name, 'readwrite', s => { s.put(value); });
    return value;
  }

  async bulkPut(name, values) {
    if (!values?.length) return 0;
    await this._tx(name, 'readwrite', s => { values.forEach(v => s.put(v)); });
    return values.length;
  }

  async remove(name, key) {
    await this._tx(name, 'readwrite', s => { s.delete(key); });
  }

  async bulkRemove(name, keys) {
    if (!keys?.length) return 0;
    await this._tx(name, 'readwrite', s => { keys.forEach(k => s.delete(k)); });
    return keys.length;
  }

  async clear(name) {
    await this._tx(name, 'readwrite', s => { s.clear(); });
  }

  async count(name) {
    const db = await this._open();
    return req(db.transaction(name, 'readonly').objectStore(name).count());
  }

  async query(name, index, value) {
    const db = await this._open();
    const store = db.transaction(name, 'readonly').objectStore(name);
    if (!store.indexNames.contains(index)) return [];
    return req(store.index(index).getAll(value));
  }

  async close() {
    if (this._dbPromise) {
      const db = await this._dbPromise;
      db.close();
      this._dbPromise = null;
    }
  }
}
